import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

// SERVICES
import {
  ICategory,
  ISubCategory,
  IRfqVariantInterface,
} from '../../../../rfq/interfaces';
import { EnvironmentUtilService } from '../../util';
import { ApiService } from '../api.service';

// INTERFACES

@Injectable({
  providedIn: 'root',
})
export class RfqApiService {
  private readonly _currentRfq = new BehaviorSubject<any>(<any>{});
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _submittingForm = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _categoryLoading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _subCategoryLoading = new BehaviorSubject<boolean>(
    <boolean>false,
  );

  private readonly _loadingWarehouseGroupsOnSelect = new BehaviorSubject<boolean>(
    <boolean>false,
  );
  private readonly _rfqs = new BehaviorSubject<any[]>(<any[]>[]);
  private readonly _variantRfq = new BehaviorSubject<any[]>(<any[]>[]);
  private readonly _categories = new BehaviorSubject<any[]>(<any[]>[]);
  private readonly _subCategories = new BehaviorSubject<any[]>(<any[]>[]);

  currentRfq$ = this._currentRfq.asObservable();
  rfqs$ = this._rfqs.asObservable();
  variantRfq$ = this._variantRfq.asObservable();
  loading$ = this._loading.asObservable();
  submittingForm$ = this._submittingForm.asObservable();
  categories$ = this._categories.asObservable();
  subCategories$ = this._subCategories.asObservable();
  categoryLoading$ = this._categoryLoading.asObservable();
  subCategoryLoading$ = this._subCategoryLoading.asObservable();
  loadingWarehouseGroupsOnSelect$ =
    this._loadingWarehouseGroupsOnSelect.asObservable();

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  resetData() :void {
    this._rfqs.next([]);
    this._loading.next(false);
  }

  setRfqs(data :any) :void {
    this._rfqs.next(data);
  }

  get(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService
      .get(`/api/admin/v2/skus`, {
        params,
      })
      .pipe(
        tap((data) => data),
        catchError((error) => error),
      );
  }

  getV2(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService
      .get(`${this.environment.urlV2}/rfqs`, {
        params,
      })
      .pipe(
        tap((data) => data),
        catchError((error) => error),
      );
  }

  getById(id :number) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV1}/rfqs/${id}`).pipe(
      tap((data) => data),
      catchError((error) => error),
    );
  }

  searchAsync(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV1}/rfqs/search_sku_dropdown`,
      { params },
    );
  }

  search(params :{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV1}/rfqs/search_sku_dropdown`, { params })
      .subscribe({
        next: (response :any[]) => {
          this._rfqs.next(response);
          this._loading.next(false);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  searchSKU(params :any) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV1}/rfqs/search_sku`, {
      params: params,
    });
  }

  plainSearch(params :{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV1}/rfqs/search`, { params })
      .subscribe({
        next: (response :any[]) => {
          this._rfqs.next(response);
          this._loading.next(false);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  getCategories(params :any) :Subscription {
    this._categoryLoading.next(true);

    return this.apiService
      .get(`${this.environment.urlV1}/product_categories`, { params })
      .subscribe({
        next: (response :ICategory[]) => {
          this._categories.next(response);
          this._categoryLoading.next(false);
        },
        error: (error) => {
          this._categoryLoading.next(false);
        },
      });
  }

  getSubcategories(params :any) :Subscription {
    this._subCategoryLoading.next(true);

    return this.apiService
      .get(`${this.environment.urlV1}/subcategories`, { params })
      .subscribe({
        next: (response :ISubCategory[]) => {
          this._subCategories.next(response);
          this._subCategoryLoading.next(false);
        },
        error: (error) => {
          this._subCategoryLoading.next(false);
        },
      });
  }

  create(payload :FormData) :Observable<any> {
    this._submittingForm.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV1}/rfqs`,
        body: payload,
        contentType: 'multipart/form-data',
      })
      .pipe(
        tap({
          next: (data) => {
            this._variantRfq.next(data?.record);
            this._submittingForm.next(false);
          },
          error: (error) => {
            this._submittingForm.next(false);
          },
        }),
      );
  }

  uploadAttachments(data :{ id :number; payload :FormData }) :Observable<any> {
    const { id, payload } = data;

    return this.apiService
      .post({
        path: `${this.environment.urlV1}/rfqs/${id}/attachments`,
        body: payload,
        contentType: 'multipart/form-data',
      })
      .pipe(
        tap({
          next: (data) => {},
          error: (error) => {},
        }),
      );
  }

  createVariant(payload :IRfqVariantInterface) :Observable<any> {
    this._submittingForm.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV1}/rfqs/variant`,
        body: payload,
      })
      .pipe(
        tap({
          next: (data) => {
            this._currentRfq.next(data?.record);
            this._submittingForm.next(false);
          },
          error: (error) => {
            this._submittingForm.next(false);
          },
        }),
      );
  }

  setSubcategories(data :ISubCategory[]) :void {
    this._subCategories.next(data || []);
  }

  getLocationStocksByRfqIds(params :{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV1}/rfqs/location-stocks-by-rfq-ids`,
      { params },
    );
  }

  setWarehouseGroupOnChangeLoading(value :boolean) :void {
    this._loadingWarehouseGroupsOnSelect.next(value);
  }

  addAlternateSku(data :{
    rfqId :number;
    sku :string;
    alternateSku :string;
  }) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/products/alternate-sku`,
      body: data,
    });
  }

  searchAsyncV2(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/products/search-sku-dropdown`,
      { params },
    );
  }

  attachBOMRfq(payload :{ rfqId :number; bomId :number }) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/rfqs/attach-bom`,
      body: payload,
      contentType: 'multipart/form-data',
    });
  }

  syncBomsRfq(rfqId :number, payload :{ bomIds :number[] }) :Observable<any> {
    return this.apiService.put(
      `${this.environment.urlV2}/rfqs/${rfqId}/sync-boms`,
      payload,
    );
  }

  getRfqStockSnapshot(params :{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV2}/reports/stock-snapshot`, {
      params,
    });
  }

  resetBaselineToZero(params :any) :Observable<any> {
    return this.apiService.put(
      `${this.environment.urlV1}/rfqs/resetBaselineToZero`,
      {
        params: params,
      },
    );
  }

  exportAlternateSku(params ?:{
    [key :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.getBlob(
      `${this.environment.urlV2}/products/exports/alternate-sku`,
      {
        params: params,
      },
    );
  }

  exportStockSnapshot(params ?:{
    [key :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.getBlob(
      `${this.environment.urlV2}/reports/stock-snapshot/export`,
      {
        params: params,
      },
    );
  }
}
